<template>
  <div class="user-page">
    <!-- Header: Account Title -->
    <div class="page-header">
       <span class="header-icon"><van-icon name="manager-o" /></span>
       <span class="header-title">账号</span>
    </div>

    <!-- Anonymous ID Row -->
    <div class="id-row">
       <span class="label">用户号:</span>
       <span class="value">{{ anonymousId }}</span>
       <span class="copy-tag" @click="copyText(anonymousId)">复制</span>
    </div>

    <!-- Main Card -->
    <div class="main-card">
       <div class="card-content">
          <div class="avatar-col">
             <img src="https://img.yzcdn.cn/vant/cat.jpeg" class="avatar-img" />
          </div>
          <div class="info-col">
             <!-- Logged In State -->
             <div v-if="isLoggedIn">
                <div class="username-row">
                   {{ user.username }} 
                   <span class="copy-tag-sm" @click="copyText(user.username)">复制</span>
                </div>
                <div class="member-row">会员: <span class="orange-text">{{ formatMemberDate(user.memberDate) }}</span></div>
                <div class="coin-row">金币: <span class="orange-text">{{ user.coins }}</span></div>
             </div>
             <!-- Unlogged State -->
             <div v-else>
                <div class="username-row">未登录</div>
                <div class="member-row">会员: <span class="orange-text">非会员</span></div>
                <div class="coin-row">金币: <span class="orange-text">0</span></div>
             </div>
          </div>
       </div>

       <!-- Action Button -->
       <div class="action-btn-row">
          <!-- 只有正式登录用户才显示退出按钮，自动注册用户仍显示登录/注册 -->
          <button v-if="isLoggedIn && !user.isAutoRegistered" class="btn-logout" @click="logout">退出</button>
          <button v-else class="btn-login" @click="showLogin = true">登录/注册</button>
       </div>

       <!-- Warning for Unlogged or Auto-registered -->
       <div v-if="!isLoggedIn || user.isAutoRegistered" class="warning-box">
          <div class="warn-title">温馨提示:</div>
          <div class="warn-text pink-text">为防止账号丢失</div>
          <div class="warn-text pink-text">请绑定手机号</div>
       </div>
    </div>

    <!-- Promoter Dashboard Section -->
    <div v-if="isPromoter && promoterData" class="promoter-dashboard">
       <div class="promo-header">
          <span>📈 推广统计</span>
          <button class="refresh-btn" @click="refreshPromoterStats">🔄</button>
       </div>
       <div class="promo-info">
          <div class="promo-id">推广ID: <span class="orange-text">{{ promoterData.promoterId }}</span></div>
          <div class="promo-name">名称: {{ promoterData.name }}</div>
       </div>
       <div class="promo-stats">
          <div class="promo-stat-item">
             <div class="promo-stat-value">{{ promoterData.totalVisits }}</div>
             <div class="promo-stat-label">访问量</div>
          </div>
          <div class="promo-stat-item">
             <div class="promo-stat-value">{{ promoterData.uniqueIps }}</div>
             <div class="promo-stat-label">独立IP</div>
          </div>
          <div class="promo-stat-item">
             <div class="promo-stat-value">{{ promoterData.registeredUsers }}</div>
             <div class="promo-stat-label">注册</div>
          </div>
          <div class="promo-stat-item">
             <div class="promo-stat-value">{{ promoterData.totalPayments }}</div>
             <div class="promo-stat-label">订单</div>
          </div>
       </div>
       <div class="promo-earnings">
          <div class="earning-row">
             <span>购买金额</span>
             <span class="amount">¥{{ promoterData.totalAmount }}</span>
          </div>
          <div class="earning-row highlight">
             <span>分润金额 ({{ promoterData.commissionRate }}%)</span>
             <span class="amount green-amount">¥{{ promoterData.commissionAmount }}</span>
          </div>
       </div>
       <div class="promo-link-section">
          <div class="link-label">你的推广链接:</div>
          <div class="link-row">
             <span class="link-text">{{ getPromoLink() }}</span>
             <button class="copy-promo-btn" @click="copyPromoLink">复制</button>
          </div>
       </div>
       <div v-if="promoterData.customDomain" class="promo-link-section" style="margin-top: 10px;">
          <div class="link-label">你的专属推广域名:</div>
          <div class="link-row">
             <span class="link-text">{{ promoterData.customDomain }}</span>
             <button class="copy-promo-btn" @click="copyCustomDomain">复制</button>
          </div>
       </div>
    </div>

    <!-- Login Modal -->
    <div v-if="showLogin" class="modal-mask">
       <div class="modal-box">
          <div class="modal-close" @click="showLogin = false"><van-icon name="cross" /></div>
          <div class="modal-title">绑定手机号 防丢失会员</div>
          <div class="modal-subtitle">注册/登录</div>
          
          <div class="form-group">
             <div class="input-label">手机号</div>
             <input type="tel" v-model="loginForm.phone" class="dark-input" />
          </div>
          <div class="form-group">
             <div class="input-label">密码</div>
             <input type="password" v-model="loginForm.password" class="dark-input" />
          </div>
          
          <button class="btn-submit" @click="handleLogin">注册/登录</button>
       </div>
    </div>

    <!-- Bottom Nav (Custom Style) -->
    <div class="bottom-nav">
      <div class="nav-item" @click="$router.push('/home').catch(()=>{})">
        <div class="nav-btn btn-dark">首页</div>
      </div>
      <div class="nav-item" @click="$router.push('/purchased')">
        <div class="nav-btn btn-yellow">已购买</div>
      </div>
      <div class="nav-item">
        <div class="nav-btn btn-yellow">账号</div>
      </div>
      <div class="nav-item" @click="showBookmarkModal = true">
        <div class="nav-btn btn-dark">收藏本站</div>
      </div>
    </div>

     <!-- Bookmark Modal -->
    <div class="modal-mask" v-if="showBookmarkModal">
       <div class="modal-box">
          <div class="modal-header">
             <span>收藏本站 永不迷路</span>
             <div class="close-btn" @click="showBookmarkModal = false">
               <van-icon name="cross" size="20" color="#fff" />
             </div>
          </div>
          <div class="modal-body">
             <div class="green-text">
                重要提示!!!请截图保存本站最新网址! ! ! 已付款的请在已购处播放!
             </div>
             <div class="orange-label">永久网址:</div>
             <div class="url-row">
                <span class="url-text">http://36x3.vip</span>
                <button class="copy-btn" @click="copyUrl">复制</button>
             </div>
             <div class="qr-label">截图保存本站二维码:</div>
             <div class="qr-box">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=http://36x3.vip" alt="QR" />
             </div>
          </div>
       </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'User',
  data() {
    return {
      anonymousId: '',
      isLoggedIn: false,

      showLogin: false,
      showBookmarkModal: false,
      user: {
        username: '',
        memberDate: '',
        coins: 0,
        isAutoRegistered: false
      },
      isPromoter: false,
      promoterData: null,
      loginForm: {
        phone: '',
        password: ''
      }
    }
  },
  created() {
    this.initUser();
  },
  methods: {
    initUser() {
        // 1. Anonymous ID
        let anon = localStorage.getItem('anonymousId');
        if (!anon) {
            anon = Math.random().toString(16).substr(2, 8);
            localStorage.setItem('anonymousId', anon);
        }
        this.anonymousId = anon;

        // 2. Login Token
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        if (token && userStr) {
            this.isLoggedIn = true;
            this.user = JSON.parse(userStr);
            
            // Restore promoter state
            const isPromoter = localStorage.getItem('isPromoter');
            const promoterDataStr = localStorage.getItem('promoterData');
            if (isPromoter === 'true' && promoterDataStr) {
                this.isPromoter = true;
                this.promoterData = JSON.parse(promoterDataStr);
            }
        } else {
            this.isLoggedIn = false;
            this.isPromoter = false;
            this.promoterData = null;
        }
    },
    copyText(text) {
        const input = document.createElement('input');
        input.setAttribute('value', text);
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.$toast('复制成功');
    },
    async handleLogin() {
        if (!this.loginForm.phone || !this.loginForm.password) {
            this.$toast('请输入账号密码');
            return;
        }
        try {
            const deviceId = localStorage.getItem('deviceId');
            const promoterId = localStorage.getItem('promoterId');
            
            const { data } = await this.$http.post('/login', {
                username: this.loginForm.phone,
                password: this.loginForm.password,
                deviceId,
                referredBy: promoterId // Pass promoter ID for new user registration
            });
            
            if (data.success) {
                this.isLoggedIn = true;
                this.user = {
                    username: data.user.username,
                    memberDate: data.user.memberDate,
                    coins: data.user.coins || 0,
                    isAutoRegistered: data.user.isAutoRegistered || false
                };
                
                // Check if user is a promoter
                if (data.isPromoter && data.promoterData) {
                    this.isPromoter = true;
                    this.promoterData = data.promoterData;
                    localStorage.setItem('isPromoter', 'true');
                    localStorage.setItem('promoterData', JSON.stringify(data.promoterData));
                } else {
                    this.isPromoter = false;
                    this.promoterData = null;
                    localStorage.removeItem('isPromoter');
                    localStorage.removeItem('promoterData');
                }
                
                localStorage.setItem('token', 'mock_token_' + Date.now());
                localStorage.setItem('user', JSON.stringify(this.user));
                this.showLogin = false;
                this.$toast.success(data.isNew ? '注册成功！' : '登录成功');
            } else {
                this.$toast.fail('登录失败: ' + data.message);
            }
        } catch (e) {
            this.$toast.fail('网络错误');
        }
    },
    logout() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('isPromoter');
        localStorage.removeItem('promoterData');
        this.isLoggedIn = false;
        this.isPromoter = false;
        this.promoterData = null;
        this.user = {};
        this.$toast('已退出');
    },
    getPromoLink() {
        if (!this.promoterData) return '';
        return `${window.location.origin}/?ref=${this.promoterData.promoterId}`;
    },
    copyPromoLink() {
        const link = this.getPromoLink();
        navigator.clipboard.writeText(link).then(() => {
            this.$toast.success('推广链接已复制');
        }).catch(() => {
            const input = document.createElement('input');
            input.setAttribute('value', link);
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            this.$toast.success('推广链接已复制');
        });
    },
    copyCustomDomain() {
        if (!this.promoterData?.customDomain) return;
        const domain = this.promoterData.customDomain;
        navigator.clipboard.writeText(domain).then(() => {
            this.$toast.success('专属域名已复制');
        }).catch(() => {
            const input = document.createElement('input');
            input.setAttribute('value', domain);
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            this.$toast.success('专属域名已复制');
        });
    },
    async refreshPromoterStats() {
        if (!this.user.username) return;
        try {
            const { data } = await this.$http.get('/promoter/stats', {
                params: { username: this.user.username }
            });
            this.promoterData = data;
            localStorage.setItem('promoterData', JSON.stringify(data));
            this.$toast.success('统计已刷新');
        } catch (e) {
            console.error('Failed to refresh promoter stats:', e);
        }
    },
    toggleBookmark() {
        this.showBookmarkModal = true;
    },
    formatMemberDate(dateStr) {
        if (!dateStr) return '未购买';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '未购买';
        const now = new Date();
        if (date <= now) return '已过期';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const h = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `到期: ${y}-${m}-${d} ${h}:${min}`;
    },
    copyUrl() {
        const input = document.createElement('input');
        input.setAttribute('value', 'http://36x3.vip');
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.$toast.success('复制成功');
    }
  }
}
</script>

<style scoped>
.user-page {
    background-color: #111;
    min-height: 100vh;
    color: #fff;
    font-family: sans-serif;
    padding-bottom: 60px;
}
.page-header {
    padding: 15px;
    font-size: 18px;
    font-weight: bold;
    display: flex;
    align-items: center;
    color: #ddd;
}
.header-icon { margin-right: 5px; color: #ffaa00; font-size: 20px;}

.id-row {
    background-color: #000;
    padding: 10px 15px;
    font-size: 16px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid #222;
}
.id-row .label { color: #aaa; margin-right: 10px; }
.id-row .value { color: #ffaa00; font-weight: bold; font-size: 20px; margin-right: 10px;}
.copy-tag {
    background-color: #0000ff;
    color: white;
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    cursor: pointer;
}

.main-card {
    padding: 20px;
}
.card-content {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
}
.avatar-img {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    border: 2px solid #333;
}
.info-col {
    margin-left: 15px;
}
.username-row {
    font-size: 20px;
    font-weight: bold;
    color: #fff;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 5px;
}
.copy-tag-sm {
    background-color: #0000ff;
    color: white;
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 8px;
}
.member-row, .coin-row {
    font-size: 14px;
    color: #ccc;
    line-height: 1.5;
}
.orange-text { color: #ffaa00; }

.action-btn-row {
    margin-top: 20px;
}
.btn-login {
    width: 100%;
    background-color: #ffaa00;
    color: #000;
    border: none;
    padding: 10px;
    font-size: 16px;
    font-weight: bold;
    border-radius: 5px;
}
.btn-logout {
    width: 100%;
    background-color: #333;
    color: #fff;
    border: none;
    padding: 10px;
    font-size: 16px;
    border-radius: 5px;
}

.warning-box {
    margin-top: 20px;
}
.warn-title { color: #fff; font-size: 16px; font-weight: bold; margin-bottom: 5px;}
.warn-text { font-size: 24px; line-height: 1.3;}
.pink-text { color: #ff00ff; }

/* Modal */
.modal-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
}
.modal-box {
    width: 80%;
    background: #111;
    border: 1px solid #333;
    padding: 20px;
    border-radius: 5px;
    position: relative;
    text-align: center;
}
.modal-close {
    position: absolute;
    right: 10px;
    top: 10px;
    color: #fff;
    font-size: 20px;
}
.modal-title {
    color: #ffaa00;
    font-weight: bold;
    margin-bottom: 20px;
    background: #222;
    padding: 10px;
    border-radius: 2px;
}
.modal-subtitle {
    color: #ffaa00;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
}
.form-group {
    margin-bottom: 15px;
    text-align: left;
}
.input-label { color: #fff; margin-bottom: 5px;}
.dark-input {
    width: 100%;
    background: #222;
    border: 1px solid #444;
    color: #fff;
    padding: 10px;
    box-sizing: border-box;
    border-radius: 4px;
}
.btn-submit {
    width: 100%;
    background: #ffaa00;
    color: #000;
    border: none;
    padding: 12px;
    font-size: 16px;
    font-weight: bold;
    border-radius: 4px;
    margin-top: 10px;
}

/* Bottom Nav */
.bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: #111;
    display: flex;
    padding: 5px 0;
    border-top: 1px solid #333;
    z-index: 100;
}
.nav-item {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
}
.nav-btn {
    width: 80%;
    text-align: center;
    padding: 8px 0;
    font-size: 14px;
    border-radius: 4px;
    font-weight: bold;
}
.btn-dark {
    background-color: #222;
    color: white;
    border: 1px solid #444;
}
.btn-yellow {
    background-color: #ffaa00;
    color: black;
}

/* Bookmark Modal Styles */
.modal-mask {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}
.modal-box {
    background: #1a1a2e;
    border-radius: 8px;
    width: 90%;
    max-width: 400px;
    overflow: hidden;
}
.modal-header {
    background: #222;
    color: #ffa500;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: bold;
}
.close-btn { cursor: pointer; }
.modal-body {
    padding: 15px;
    text-align: center;
}
.green-text {
    color: #00ff00;
    font-size: 14px;
    margin-bottom: 10px;
}
.orange-label {
    color: #ffa500;
    margin: 10px 0 5px;
}
.url-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}
.url-text {
    color: #4fc3f7;
    font-size: 20px;
    font-weight: bold;
}
.copy-btn {
    background: #ffa500;
    color: #000;
    border: none;
    padding: 5px 15px;
    border-radius: 15px;
    cursor: pointer;
}
.qr-label {
    color: #ffa500;
    margin: 10px 0;
}
.qr-box img {
    background: white;
    padding: 10px;
    border-radius: 5px;
}

/* Promoter Dashboard Styles */
.promoter-dashboard {
    background: linear-gradient(135deg, #1a1a2e, #16213e);
    margin: 15px;
    border-radius: 12px;
    padding: 15px;
    border: 1px solid #333;
}
.promo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #ffaa00;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
    border-bottom: 1px solid #333;
    padding-bottom: 10px;
}
.refresh-btn {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
}
.promo-info {
    margin-bottom: 15px;
}
.promo-id, .promo-name {
    color: #ccc;
    font-size: 14px;
    margin-bottom: 5px;
}
.promo-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-bottom: 15px;
}
.promo-stat-item {
    background: #0f3460;
    padding: 12px 5px;
    border-radius: 8px;
    text-align: center;
}
.promo-stat-value {
    font-size: 22px;
    font-weight: bold;
    color: #fff;
}
.promo-stat-label {
    font-size: 11px;
    color: #aaa;
    margin-top: 3px;
}
.promo-earnings {
    background: #0f3460;
    border-radius: 8px;
    padding: 10px 15px;
    margin-bottom: 15px;
}
.earning-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    color: #ccc;
    font-size: 14px;
    border-bottom: 1px solid #333;
}
.earning-row:last-child {
    border-bottom: none;
}
.earning-row.highlight {
    color: #fff;
    font-weight: bold;
}
.earning-row .amount {
    color: #ffaa00;
    font-weight: bold;
}
.earning-row .green-amount {
    color: #4CAF50;
}
.promo-link-section {
    background: #0f3460;
    border-radius: 8px;
    padding: 12px;
}
.link-label {
    color: #aaa;
    font-size: 12px;
    margin-bottom: 8px;
}
.link-row {
    display: flex;
    align-items: center;
    gap: 10px;
}
.link-text {
    flex: 1;
    background: #222;
    padding: 8px;
    border-radius: 4px;
    color: #4fc3f7;
    font-size: 12px;
    word-break: break-all;
}
.copy-promo-btn {
    background: #ffaa00;
    color: #000;
    border: none;
    padding: 8px 15px;
    border-radius: 15px;
    font-weight: bold;
    cursor: pointer;
}
</style>
